/* ============================================================
   THE TEDDY SOCIETY — Find Your Bear builder
   File: assets/tts-builder.js
   Multi-step funnel:
     bear -> garment -> size -> review
   (The old "route" choice step — "I know my bear" vs "Help me
   choose" — has been removed. The builder now opens straight on
   the full bear grid. The route/guided markup and settings still
   exist in the section file for now but are never activated, so
   nothing extra loads or renders.)
   Bears and garments are both embedded server-side (no product
   fetches needed): bears come from the bear_character metaobjects,
   garments come from the 4 shared bear-garment products, each
   carrying its own full variant list. Picking a bear just fixes the
   "Design" option; picking a garment + size resolves a REAL variant.
   Requires tts-cart.js.

   Analytics: dispatches document CustomEvents named per the
   analytics plan (tts_builder_started etc) with a detail payload,
   ready to forward from a custom pixel.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'tts-builder-state-v2';

  function track(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, detail || {}));
      }
    } catch (e) { /* analytics must never break the funnel */ }
  }

  var Builder = function () {
    var instance = Reflect.construct(HTMLElement, [], Builder);
    return instance;
  };
  Builder.prototype = Object.create(HTMLElement.prototype);
  Builder.prototype.constructor = Builder;
  Object.setPrototypeOf(Builder, HTMLElement);

  Builder.prototype.connectedCallback = function () {
    var dataEl = this.querySelector('[data-tts-builder-data]');
    if (!dataEl) return;
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this.moneyFormat = this.getAttribute('data-money-format') || '£{{amount}}';
    this.guidedEnabled = this.getAttribute('data-guided') === 'true';
    this.state = {
      step: 'bear',
      bear: null,      // bear object from data.bears
      garment: null,   // garment object from data.garments (includes options + variants)
      options: {},      // { optionName: value } — Design is pre-filled from bear.name
      quantity: 1
    };

    this.steps = {};
    var self = this;
    this.querySelectorAll('[data-tts-step]').forEach(function (el) {
      self.steps[el.getAttribute('data-tts-step')] = el;
    });

    this.statusEl = this.querySelector('[data-tts-builder-status]');
    this.liveEl = this.querySelector('[data-tts-builder-live]');
    this.progressFill = this.querySelector('[data-tts-progress-fill]');
    this.progressText = this.querySelector('[data-tts-progress-text]');
    this.backBtn = this.querySelector('[data-tts-builder-back]');

    this.bindEvents();
    this.restoreState();
    track('tts_builder_started', { source: new URLSearchParams(location.search).get('source') || 'direct' });
  };

  /* --------------------------------------------------------
     State persistence + deep links
     -------------------------------------------------------- */

  Builder.prototype.restoreState = function () {
    var params = new URLSearchParams(window.location.search);
    var bearParam = params.get('bear');
    var garmentParam = params.get('garment');

    // How did the shopper get here? A fresh link click (navigation
    // type "navigate" — e.g. the "Find Your Bear" nav link) must
    // ALWAYS start the funnel from the top. Auto-resuming a previous
    // session on a fresh visit bounced shoppers straight back to the
    // garment step for the bear they picked earlier, with no obvious
    // way to choose a different bear — it read as being stuck in a
    // loop. Only a page refresh or browser back/forward should
    // silently pick up where they left off. Explicit ?bear= deep
    // links (vibe chips, bear carousel, LTD gallery) still work —
    // they carry the shopper's intent in the URL itself.
    var navType = 'navigate';
    try {
      var navEntries = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navEntries && navEntries.length) {
        navType = navEntries[0].type;
      } else if (window.performance && performance.navigation) {
        navType = performance.navigation.type === 1 ? 'reload' : (performance.navigation.type === 2 ? 'back_forward' : 'navigate');
      }
    } catch (e) { /* ignore — treat as a fresh navigation */ }
    var allowResume = navType === 'reload' || navType === 'back_forward';

    // Fallback: if the ?bear= query string didn't survive navigation
    // (theme preview link rewriting, caching, redirects), tts-bear-link.js
    // stashes the intended bear handle in sessionStorage at click time.
    if (!bearParam) {
      try {
        var pendingBear = window.sessionStorage.getItem('tts-pending-bear');
        if (pendingBear) {
          bearParam = pendingBear;
          window.sessionStorage.removeItem('tts-pending-bear');
        }
      } catch (e) { /* ignore */ }
    } else {
      try { window.sessionStorage.removeItem('tts-pending-bear'); } catch (e) { /* ignore */ }
    }

    var saved = null;
    if (allowResume) {
      try { saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)); } catch (e) { /* ignore */ }
    } else {
      // Fresh navigation — any previously saved session is stale.
      // Clear it so it can't hijack this or a later visit.
      try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    }

    if (bearParam) {
      var bear = this.data.bears.find(function (b) { return b.handle === bearParam; });
      if (bear) {
        this.state.bear = bear;
        if (garmentParam) {
          var garment = this.data.garments.find(function (g) { return g.handle === garmentParam; });
          if (garment) {
            this.selectGarment(garment, true);
            return;
          }
        }
        this.goTo('garment');
        return;
      }
    }

    if (saved && saved.bearHandle) {
      var savedBear = this.data.bears.find(function (b) { return b.handle === saved.bearHandle; });
      if (savedBear) {
        this.state.bear = savedBear;
        if (saved.garmentHandle) {
          var savedGarment = this.data.garments.find(function (g) { return g.handle === saved.garmentHandle; });
          if (savedGarment) {
            this.selectGarment(savedGarment, true);
            return;
          }
        }
        this.goTo('garment');
        return;
      }
    }

    this.goTo('bear');
  };

  Builder.prototype.saveState = function () {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        bearHandle: this.state.bear ? this.state.bear.handle : null,
        garmentHandle: this.state.garment ? this.state.garment.handle : null
      }));
    } catch (e) { /* private mode etc. */ }

    // Keep the URL shareable. We only ever ADD params here, never
    // silently remove them — stripping ?bear= the moment a match
    // fails would erase the evidence of what was actually clicked,
    // which makes this flow impossible to debug from the address bar.
    // Explicit resets (fullReset) clear the URL themselves instead.
    if (window.history && window.history.replaceState && (this.state.bear || this.state.garment)) {
      var url = new URL(window.location.href);
      if (this.state.bear) url.searchParams.set('bear', this.state.bear.handle);
      if (this.state.garment) url.searchParams.set('garment', this.state.garment.handle);
      window.history.replaceState({}, '', url.toString());
    }
  };

  /* --------------------------------------------------------
     Navigation
     -------------------------------------------------------- */

  var PROGRESS = { bear: 1, guided: 1, garment: 2, variant: 3, review: 4, success: 4 };

  Builder.prototype.goTo = function (step) {
    var self = this;
    this.state.step = step;
    Object.keys(this.steps).forEach(function (key) {
      self.steps[key].classList.toggle('is-active', key === step);
    });

    // Progress
    var total = 4;
    var current = PROGRESS[step] || 0;
    if (this.progressFill) this.progressFill.style.width = (current / total * 100) + '%';
    if (this.progressText) {
      this.progressText.textContent = current > 0 && current < 5
        ? (this.progressText.getAttribute('data-template') || 'Step {c} of {t}').replace('{c}', current).replace('{t}', total)
        : '';
    }

    if (this.backBtn) this.backBtn.hidden = (step === 'bear' || step === 'success');

    // Move focus to step heading for screen readers
    var heading = this.steps[step] && this.steps[step].querySelector('[data-tts-step-heading]');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: false });
    }

    this.clearStatus();
    this.renderStep(step);
    this.saveState();
  };

  Builder.prototype.goBack = function () {
    switch (this.state.step) {
      case 'guided':
        this.goTo('bear'); break;
      case 'garment':
        this.goTo('bear'); break;
      case 'variant':
        this.goTo('garment'); break;
      case 'review':
        this.goTo('variant'); break;
      default:
        this.goTo('bear');
    }
  };

  /* --------------------------------------------------------
     Event wiring
     -------------------------------------------------------- */

  Builder.prototype.bindEvents = function () {
    var self = this;

    this.addEventListener('click', function (event) {
      var route = event.target.closest('[data-tts-route]');
      if (route) {
        var mode = route.getAttribute('data-tts-route');
        track('tts_builder_mode_selected', { mode: mode });
        self.goTo(mode === 'guided' ? 'guided' : 'bear');
        return;
      }

      var bearBtn = event.target.closest('[data-tts-bear]');
      if (bearBtn) {
        self.selectBear(bearBtn.getAttribute('data-tts-bear'));
        return;
      }

      var filterBtn = event.target.closest('[data-tts-bear-filter]');
      if (filterBtn) {
        self.filterBears(filterBtn);
        return;
      }

      var quizBtn = event.target.closest('[data-tts-quiz-answer]');
      if (quizBtn) {
        track('tts_guided_answered', { answer: quizBtn.getAttribute('data-tts-quiz-answer') });
        self.applyQuizAnswer(quizBtn.getAttribute('data-tts-quiz-answer'));
        return;
      }

      var garmentBtn = event.target.closest('[data-tts-garment]');
      if (garmentBtn) {
        var handle = garmentBtn.getAttribute('data-tts-garment');
        var garment = self.data.garments.find(function (g) { return g.handle === handle; });
        if (garment) self.selectGarment(garment, false);
        return;
      }

      var editBtn = event.target.closest('[data-tts-edit]');
      if (editBtn) {
        self.goTo(editBtn.getAttribute('data-tts-edit'));
        return;
      }

      if (event.target.closest('[data-tts-builder-back]')) {
        self.goBack();
        return;
      }

      if (event.target.closest('[data-tts-variant-continue]')) {
        self.tryContinueToReview();
        return;
      }

      if (event.target.closest('[data-tts-builder-add]')) {
        self.addToBag();
        return;
      }

      if (event.target.closest('[data-tts-pick-another]')) {
        track('tts_pick_another_bear', {});
        self.resetForAnotherBear();
        return;
      }

      if (event.target.closest('[data-tts-start-again]')) {
        self.fullReset();
        return;
      }

      var qtyBtn = event.target.closest('[data-tts-qty-change]');
      if (qtyBtn) {
        var dir = qtyBtn.getAttribute('data-tts-qty-change');
        self.state.quantity = Math.max(1, self.state.quantity + (dir === 'up' ? 1 : -1));
        var qtyEl = self.querySelector('[data-tts-builder-qty]');
        if (qtyEl) qtyEl.value = self.state.quantity;
        self.renderReview();
        return;
      }
    });

    this.addEventListener('change', function (event) {
      if (event.target.matches('[data-tts-builder-option]')) {
        var name = event.target.getAttribute('data-option-name');
        self.state.options[name] = event.target.value;
        if (/size/i.test(name)) track('tts_size_selected', { value: event.target.value });
        self.refreshVariantUI();
      }
      if (event.target.matches('[data-tts-builder-qty]')) {
        var v = parseInt(event.target.value, 10);
        self.state.quantity = isNaN(v) || v < 1 ? 1 : v;
      }
    });

    this.addEventListener('input', function (event) {
      if (event.target.matches('[data-tts-bear-search]')) {
        self.searchBears(event.target.value);
      }
    });
  };

  /* --------------------------------------------------------
     Step 1 — bears
     -------------------------------------------------------- */

  Builder.prototype.selectBear = function (handle) {
    var bear = this.data.bears.find(function (b) { return b.handle === handle; });
    if (!bear) return;
    this.state.bear = bear;
    this.state.garment = null;
    this.state.options = {};
    track('tts_bear_selected', { bear: bear.handle, tags: bear.tags || '', maturity: bear.mature ? 'mature' : 'standard' });

    this.querySelectorAll('[data-tts-bear]').forEach(function (card) {
      card.classList.toggle('is-selected', card.getAttribute('data-tts-bear') === handle);
      card.setAttribute('aria-pressed', card.getAttribute('data-tts-bear') === handle ? 'true' : 'false');
    });

    this.announce(bear.name + ' selected.');
    var self = this;
    window.setTimeout(function () { self.goTo('garment'); }, 150);
  };

  Builder.prototype.filterBears = function (button) {
    var tag = button.getAttribute('data-tts-bear-filter');
    this.querySelectorAll('[data-tts-bear-filter]').forEach(function (b) {
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
      b.classList.toggle('is-active', b === button);
    });
    this.querySelectorAll('[data-tts-bear]').forEach(function (card) {
      var tags = (card.getAttribute('data-tags') || '').split(',');
      card.classList.toggle('is-hidden', tag !== 'all' && tags.indexOf(tag) === -1);
    });
  };

  Builder.prototype.searchBears = function (query) {
    query = (query || '').trim().toLowerCase();
    this.querySelectorAll('[data-tts-bear]').forEach(function (card) {
      var haystack = (card.getAttribute('data-search') || '').toLowerCase();
      card.classList.toggle('is-hidden', query.length > 0 && haystack.indexOf(query) === -1);
    });
  };

  Builder.prototype.applyQuizAnswer = function (tag) {
    // Guided mode = one light question, then a filtered bear grid.
    this.goTo('bear');
    var target = this.querySelector('[data-tts-bear-filter="' + tag + '"]');
    if (target) {
      this.filterBears(target);
    }
    this.announce('Here are the bears that match. Pick your favourite.');
  };

  /* --------------------------------------------------------
     Step 2 — garment (already a real product + variant list)
     -------------------------------------------------------- */

  Builder.prototype.renderStep = function (step) {
    if (step === 'garment') this.renderGarments();
    if (step === 'variant') this.renderVariantStep();
    if (step === 'review') this.renderReview();
  };

  /**
   * Every garment/size combination now has its own real, fully-
   * composited product photo wired directly to the matching variant
   * (variant.featured_image) — there is no need to fall back to the
   * garment's generic product.featured_media (which is whatever image
   * happens to sit first in that product's media list, unrelated to
   * the bear currently selected). Look up any variant for this bear
   * on this garment — size doesn't matter yet, the artwork is the
   * same across sizes — and use its real photo for the thumbnail.
   */
  Builder.prototype.imageForGarment = function (garment) {
    var bear = this.state.bear;
    if (bear && garment.designOptionIndex > -1 && garment.variants) {
      var match = garment.variants.find(function (variant) {
        return variant.options[garment.designOptionIndex] === bear.name && variant.featured_image;
      });
      if (match) return match.featured_image.src;
    }
    return garment.image || (bear && bear.image) || '';
  };

  Builder.prototype.renderGarments = function () {
    var bear = this.state.bear;
    var wrap = this.querySelector('[data-tts-garment-list]');
    var bearStrip = this.querySelector('[data-tts-garment-bear]');
    if (!bear || !wrap) return;

    if (bearStrip) {
      bearStrip.innerHTML =
        (bear.image ? '<img src="' + bear.image + '" alt="" loading="lazy" width="56" height="56">' : '') +
        '<div class="tts-builder__selected-strip-meta"><strong>' + bear.name + '</strong><span>' + (bear.line || '') + '</span></div>';
    }

    var garments = this.data.garments || [];
    if (!garments.length) {
      wrap.innerHTML = '';
      this.showStatus(this.text('mappingError', 'That combination is not available. Try another size.'), 'info');
      return;
    }

    var self = this;
    var html = garments.map(function (g) {
      var img = self.imageForGarment(g);
      return (
        '<button type="button" class="tts-builder__garment" data-tts-garment="' + g.handle + '">' +
          (img ? '<img src="' + img + '" alt="" loading="lazy" width="300" height="300">' : '') +
          '<span class="tts-builder__garment-name">' + g.name + '</span>' +
          (g.fit ? '<span class="tts-builder__garment-fit">' + g.fit + '</span>' : '') +
          '<span class="tts-builder__garment-price">' + g.priceLabel + '</span>' +
        '</button>'
      );
    }).join('');
    wrap.innerHTML = html;
  };

  Builder.prototype.selectGarment = function (garment, silent) {
    this.state.garment = garment;
    this.state.options = {};
    // The bear IS the "Design" option — pre-fill it so only Size remains.
    if (this.state.bear && garment.options && garment.designOptionIndex > -1) {
      var designName = garment.options[garment.designOptionIndex];
      this.state.options[designName] = this.state.bear.name;
    }
    if (!silent) track('tts_garment_selected', { bear: this.state.bear && this.state.bear.handle, garment: garment.handle });
    this.goTo('variant');
  };

  /* --------------------------------------------------------
     Step 3 — size, from the garment's real variants
     -------------------------------------------------------- */

  Builder.prototype.renderVariantStep = function () {
    var garment = this.state.garment;
    var wrap = this.querySelector('[data-tts-variant-options]');
    var strip = this.querySelector('[data-tts-variant-bear]');
    if (!garment || !wrap) return;

    if (strip && this.state.bear) {
      strip.innerHTML =
        (this.state.bear.image ? '<img src="' + this.state.bear.image + '" alt="" loading="lazy" width="56" height="56">' : '') +
        '<div class="tts-builder__selected-strip-meta"><strong>' + this.state.bear.name + '</strong><span>' + (garment.name || '') + '</span></div>';
    }

    var self = this;
    var html = garment.options.map(function (optionName, index) {
      // The Design option is already fixed to the chosen bear — only
      // render the remaining option(s), i.e. Size.
      if (index === garment.designOptionIndex) return '';

      var values = [];
      garment.variants.forEach(function (variant) {
        var value = variant.options[index];
        if (values.indexOf(value) === -1) values.push(value);
      });
      var isSize = /size/i.test(optionName);
      var inputs = values.map(function (value, vIndex) {
        var id = 'tts-b-opt-' + index + '-' + vIndex;
        var cls = isSize ? 'tts-size' : 'tts-swatch';
        return (
          '<span class="' + cls + '">' +
            '<input type="radio" id="' + id + '" name="tts-b-option-' + index + '" value="' + value.replace(/"/g, '&quot;') + '"' +
            ' data-tts-builder-option data-option-name="' + optionName.replace(/"/g, '&quot;') + '" data-option-index="' + index + '">' +
            '<label class="' + (isSize ? 'tts-size__label' : 'tts-swatch__label') + '" for="' + id + '">' +
              (isSize ? value : '<span class="tts-swatch__dot" style="background:' + self.swatchColour(value) + '"></span>' + value) +
            '</label>' +
          '</span>'
        );
      }).join('');
      return (
        '<fieldset class="tts-option" data-option-fieldset="' + index + '">' +
          '<legend class="tts-option__label">' + optionName + '</legend>' +
          '<div class="tts-option__values">' + inputs + '</div>' +
        '</fieldset>'
      );
    }).join('');

    wrap.innerHTML = html;
    this.refreshVariantUI();
  };

  Builder.prototype.swatchColour = function (name) {
    var map = {
      'black': '#262121', 'white': '#FFFFFF', 'cream': '#F4EBDD', 'ivory': '#FFFDF9',
      'pink': '#E8B4BC', 'dusty pink': '#C68F93', 'dusty rose': '#C68F93', 'rose': '#C68F93',
      'burgundy': '#7D263B', 'red': '#B03A48', 'brown': '#563A31', 'cocoa': '#563A31',
      'grey': '#B7B2AE', 'gray': '#B7B2AE', 'charcoal': '#3D3737', 'navy': '#2C3652',
      'blue': '#AFC8D8', 'baby blue': '#AFC8D8', 'sage': '#87927C', 'green': '#87927C',
      'yellow': '#F2D27A', 'butter': '#F2D27A', 'lilac': '#C6B7D8', 'purple': '#8E7BA8',
      'beige': '#D9C8B2', 'sand': '#D9C8B2', 'oat': '#D9C8B2'
    };
    return map[(name || '').toLowerCase()] || '#D9C8B2';
  };

  Builder.prototype.currentVariant = function () {
    var garment = this.state.garment;
    if (!garment) return null;
    var self = this;
    var chosen = garment.options.map(function (name) { return self.state.options[name]; });
    if (chosen.some(function (v) { return !v; })) return null;
    return garment.variants.find(function (variant) {
      return variant.options.every(function (value, index) { return value === chosen[index]; });
    }) || null;
  };

  Builder.prototype.refreshVariantUI = function () {
    var garment = this.state.garment;
    if (!garment) return;
    var self = this;

    // Cross out sizes that cannot be purchased for this bear
    this.querySelectorAll('[data-tts-builder-option]').forEach(function (input) {
      var index = parseInt(input.getAttribute('data-option-index'), 10);
      var test = garment.options.map(function (name) { return self.state.options[name]; });
      test[index] = input.value;
      var match = garment.variants.find(function (variant) {
        return variant.options.every(function (value, i) {
          return test[i] === undefined || test[i] === null || value === test[i];
        });
      });
      var unavailable = !match || !match.available;
      var wrapper = input.closest('.tts-size, .tts-swatch');
      if (wrapper) wrapper.classList.toggle('is-unavailable', unavailable);
    });

    var variant = this.currentVariant();
    var priceEl = this.querySelector('[data-tts-variant-price]');
    var continueBtn = this.querySelector('[data-tts-variant-continue]');
    if (priceEl) {
      priceEl.textContent = variant
        ? window.TTSFormatMoney(variant.price, this.moneyFormat)
        : this.text('__priceFallback', garment.priceLabel || '');
    }
    if (continueBtn) {
      if (variant && variant.available) {
        continueBtn.removeAttribute('aria-disabled');
      } else {
        continueBtn.setAttribute('aria-disabled', 'true');
      }
    }

    if (variant && !variant.available) {
      this.showStatus(this.text('soldOut', 'That option is sold out. Your other choices are still saved.'), 'info');
    } else {
      this.clearStatus();
    }
  };

  Builder.prototype.tryContinueToReview = function () {
    var garment = this.state.garment;
    if (!garment) return;
    var self = this;
    var missing = garment.options.filter(function (name, index) {
      return index !== garment.designOptionIndex && !self.state.options[name];
    });
    var variant = this.currentVariant();

    if (missing.length) {
      this.showStatus(this.text('noSize', 'Choose a size to add your bear to the bag.'), 'error');
      return;
    }
    if (!variant) {
      this.showStatus(this.text('mappingError', 'That combination is not available. Try another size.'), 'error');
      return;
    }
    if (!variant.available) {
      this.showStatus(this.text('soldOut', 'That option is sold out. Your other choices are still saved.'), 'error');
      return;
    }
    this.goTo('review');
  };

  /* --------------------------------------------------------
     Step 4 — review + add to bag
     -------------------------------------------------------- */

  Builder.prototype.renderReview = function () {
    var wrap = this.querySelector('[data-tts-review]');
    var variant = this.currentVariant();
    var bear = this.state.bear;
    var garment = this.state.garment;
    if (!wrap || !variant || !bear || !garment) return;

    var displayImage = (variant.featured_image && variant.featured_image.src) || garment.image || bear.artwork || bear.image || '';
    var mediaHtml =
      '<div class="tts-product__preview-stage">' +
        '<div class="tts-product__main-media">' +
          (displayImage ? '<img src="' + displayImage + '" alt="' + (bear.name + ' on ' + garment.name).replace(/"/g, '&quot;') + '" loading="lazy">' : '') +
        '</div>' +
      '</div>';
    var self = this;

    var rows = [
      { key: 'Bear', value: bear.name, edit: 'bear' },
      { key: 'Fit', value: garment.name, edit: 'garment' }
    ];
    garment.options.forEach(function (name, index) {
      if (index === garment.designOptionIndex) return;
      rows.push({ key: name, value: self.state.options[name], edit: 'variant' });
    });
    rows.push({ key: 'Quantity', value: String(this.state.quantity), edit: 'variant' });

    var rowsHtml = rows.map(function (row) {
      return (
        '<div class="tts-builder__review-row"><dt>' + row.key + '</dt>' +
        '<dd>' + row.value +
        ' <button type="button" class="tts-builder__edit" data-tts-edit="' + row.edit + '">Edit<span class="tts-visually-hidden"> ' + row.key + '</span></button></dd></div>'
      );
    }).join('');

    wrap.innerHTML =
      '<div class="tts-builder__review-media">' + mediaHtml + '</div>' +
      '<div class="tts-builder__review-body">' +
        '<h3 class="tts-heading tts-heading--md">' + bear.name + ' · ' + garment.name + '</h3>' +
        '<p class="tts-price"><span class="tts-price__current">' + window.TTSFormatMoney(variant.price * this.state.quantity, this.moneyFormat) + '</span></p>' +
        '<dl class="tts-builder__review-rows">' + rowsHtml + '</dl>' +
      '</div>';
  };

  Builder.prototype.addToBag = function () {
    var self = this;
    var variant = this.currentVariant();
    var bear = this.state.bear;
    var garment = this.state.garment;
    if (!variant || !bear || !garment) {
      this.showStatus(this.text('generic', 'Something went wrong, but your choices are still here.'), 'error');
      return;
    }

    var addBtn = this.querySelector('[data-tts-builder-add]');
    if (addBtn) addBtn.classList.add('is-loading');

    var properties = {
      'Bear': bear.name,
      'Fit': garment.name,
      '_builder_version': 'v2',
      '_bear_handle': bear.handle
    };

    window.TTSCart.add([{ id: variant.id, quantity: this.state.quantity, properties: properties }])
      .then(function () {
        if (addBtn) addBtn.classList.remove('is-loading');
        track('tts_builder_completed', {
          bear: bear.handle,
          garment: garment.handle,
          variant: variant.id,
          price: variant.price
        });
        self.goTo('success');
      })
      .catch(function (error) {
        if (addBtn) addBtn.classList.remove('is-loading');
        self.showStatus(error.message || self.text('networkError', 'We could not add that to your bag. Check your connection and try again.'), 'error');
        track('tts_builder_error', { type: 'add_to_cart', message: error.message });
      });
  };

  Builder.prototype.resetForAnotherBear = function () {
    this.state.bear = null;
    this.state.garment = null;
    this.state.options = {};
    this.state.quantity = 1;
    // A deliberate reset must also clear the ?bear= / ?garment=
    // params replaceState added earlier — otherwise a refresh (or
    // saveState re-adding them) resurrects the bear the shopper just
    // walked away from.
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('bear');
      url.searchParams.delete('garment');
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, '', url.toString());
      }
    } catch (e) { /* ignore */ }
    this.saveState();
    this.goTo('bear');
  };

  Builder.prototype.fullReset = function () {
    try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    this.resetForAnotherBear();
  };

  /* --------------------------------------------------------
     Status + announcements
     -------------------------------------------------------- */

  Builder.prototype.text = function (key, fallback) {
    return (this.data.text && this.data.text[key]) || fallback;
  };

  Builder.prototype.showStatus = function (message, type) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.classList.remove('is-error', 'is-info');
    this.statusEl.classList.add(type === 'error' ? 'is-error' : 'is-info');
    this.announce(message);
  };

  Builder.prototype.clearStatus = function () {
    if (!this.statusEl) return;
    this.statusEl.textContent = '';
    this.statusEl.classList.remove('is-error', 'is-info');
  };

  Builder.prototype.announce = function (message) {
    if (!this.liveEl) return;
    var self = this;
    this.liveEl.textContent = '';
    window.setTimeout(function () { self.liveEl.textContent = message; }, 50);
  };

  if (!window.customElements.get('tts-bear-builder')) {
    window.customElements.define('tts-bear-builder', Builder);
  }
})();
